import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Camera, CameraOff } from 'lucide-react';
import api from '../lib/api';
import { useTableNavigation } from '../hooks/useTableNavigation';
import { useDraft } from '../hooks/useDraft';
import DraftStatusBadge from './DraftStatusBadge';

const RETURN_REASONS = [
  { value: 'defective', label: 'Defective' },
  { value: 'damaged', label: 'Damaged in Transit' },
  { value: 'colour_fade', label: 'Colour Fade' },
  { value: 'shrinkage', label: 'Shrinkage' },
  { value: 'stitching_issue', label: 'Stitching Issue' },
  { value: 'neck_issue', label: 'Neck Issue' },
  { value: 'bubble_after_wash', label: 'Bubble After Wash' },
  { value: 'hole', label: 'Hole' },
  { value: 'tearing', label: 'Tearing' },
  { value: 'not_moving', label: 'Not Moving' },
  { value: 'wrong_item', label: 'Wrong Item' },
  { value: 'excess', label: 'Excess Quantity' },
  { value: 'other', label: 'Other' },
];

const CreateSalesReturn = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const editSR = location.state?.editSR || null;

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);

  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [returnDate, setReturnDate] = useState(new Date().toISOString().split('T')[0]);
  const [creditNoteNumber, setCreditNoteNumber] = useState('');
  const [returnNotes, setReturnNotes] = useState('');
  const [billType, setBillType] = useState('invoice');

  const [rows, setRows] = useState([createEmptyRow(1)]);
  const [saving, setSaving] = useState(false);

  // Draft auto-save
  const { draftStatus, save: saveDraftData, clear: clearDraft } = useDraft('SR', { enabled: !editSR, initialDraftId: location.state?.draftId || null });

  const [freightCharges, setFreightCharges] = useState('');
  const [customerDesigns, setCustomerDesigns] = useState(null);
  const [agents, setAgents] = useState([]);
  const [distributors, setDistributors] = useState([]);

  const customerRef = useRef(null);
  const handleTableKeyDown = useTableNavigation('sr-invoice-table');

  // Scanner state
  const [scannerActive, setScannerActive] = useState(false);
  const scannerRef = useRef(null);
  const scannerContainerId = 'sr-qr-reader';
  const productsRef = useRef([]);
  const rowsRef = useRef(rows);
  const selectedCustomerRef = useRef(selectedCustomer);
  useEffect(() => { productsRef.current = products; }, [products]);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  useEffect(() => { selectedCustomerRef.current = selectedCustomer; }, [selectedCustomer]);

  function createEmptyRow(sr) {
    return {
      sr, product_id: '', design_code: '', designSearch: '',
      color: '', size: '', qty: '', reason: 'defective', notes: '',
      rate: '', discount: '',
      colors: [], sizes: [], sizeGroups: [], individualSizes: [],
      product: null, showDesignDropdown: false,
      source_bill: '', source_bill_date: '', source_entry_id: '',
      se_variants: [],
    };
  }

  // Build size groups by WSP for a product+color
  const SR_SIZE_ORDER = ['S','M','L','XL','2XL','3XL','4XL','5XL','6XL','7XL','8XL','9XL','Free Size'];
  const getSizeGroups = useCallback((product, color, fallbackSizes) => {
    if (!product || !color) return { groups: [], individualSizes: [] };
    const skus = product.skus?.filter(s => s.color === color) || [];
    const order = (s) => { const i = SR_SIZE_ORDER.indexOf(s); return i === -1 ? 999 : i; };
    if (skus.length === 0 && fallbackSizes && fallbackSizes.length > 0) {
      const sorted = [...fallbackSizes].sort((a, b) => order(a) - order(b));
      return { groups: [], individualSizes: sorted.map(s => ({ size: s, rate: 0 })) };
    }
    const byPrice = {};
    skus.forEach(s => {
      const price = s.wsp || 0;
      if (!byPrice[price]) byPrice[price] = [];
      byPrice[price].push(s.size);
    });
    const groups = Object.entries(byPrice)
      .filter(([, sizes]) => sizes.length > 1)
      .map(([price, sizes]) => ({
        id: `set_${sizes[0]}_${sizes[sizes.length - 1]}`,
        label: `Set: ${sizes[0]}-${sizes[sizes.length - 1]}`,
        sizes, rate: parseFloat(price), count: sizes.length,
      }));
    const individualSizes = skus.map(s => ({ size: s.size, rate: s.wsp || 0 }));
    // For forwarding designs with partial SKUs, merge with fallback sizes
    if (fallbackSizes && fallbackSizes.length > 0) {
      const skuSizeSet = new Set(individualSizes.map(s => s.size));
      for (const sz of fallbackSizes) {
        if (!skuSizeSet.has(sz)) individualSizes.push({ size: sz, rate: 0 });
      }
      individualSizes.sort((a, b) => order(a.size) - order(b.size));
    }
    return { groups, individualSizes };
  }, []);

  // Scanner: handle scanned code
  const handleScannedCode = useCallback(async (code) => {
    const prods = productsRef.current;
    const curRows = rowsRef.current;
    let scanned = code.trim().toUpperCase();
    try { const qr = JSON.parse(code.trim()); if (qr.design) scanned = qr.design.toUpperCase(); } catch {}
    let product = prods.find(p => p.design_code?.toUpperCase() === scanned);
    let matchedSku = null;
    if (!product) { for (const p of prods) { const sku = (p.skus || []).find(s => s.sku_code?.toUpperCase() === scanned); if (sku) { product = p; matchedSku = sku; break; } } }
    if (!product && (scanned.includes('_') || scanned.includes('-'))) { const parts = scanned.split(/[_-]/); if (parts.length >= 2) product = prods.find(p => p.design_code?.toUpperCase() === parts[0]); }
    if (!product) { toast.error(`Design "${scanned}" not found`); return; }

    const _skuC = (product.skus || []).map(s => s.color).filter(Boolean);
    const _imgC = Array.isArray(product.image_colors) ? product.image_colors : (product.image_colors && typeof product.image_colors === 'object' ? Object.values(product.image_colors).filter(Boolean) : []);
    const colors = [...new Set([..._skuC, ..._imgC])];
    const sizes = [...new Set((product.skus || []).map(s => s.size))];
    try {
      const params = selectedCustomerRef.current ? `?customer_id=${selectedCustomerRef.current}` : '';
      const res = await api.get(`/sales-returns/lookup-design/${product.design_code}${params}`);
      if (res.data.found) {
        sourceBill = res.data.entry_number || res.data.invoice_number || '';
        sourceBillDate = res.data.entry_date || '';
        sourceEntryId = res.data.entry_id || '';
        seVariants = res.data.se_variants || [];
      }
    } catch { /* ignore */ }

    const emptyIdx = curRows.findIndex(r => !r.product_id);
    setRows(prev => {
      if (emptyIdx >= 0) {
        const next = [...prev];
        next[emptyIdx] = {
          ...next[emptyIdx], product_id: product.id, design_code: product.design_code,
          designSearch: product.design_code, colors, sizes, product,
          showDesignDropdown: false, color: matchedSku?.color || '', size: matchedSku?.size || '',
          source_bill: sourceBill, source_bill_date: sourceBillDate, source_entry_id: sourceEntryId,
          se_variants: seVariants,
        };
        return next;
      } else {
        const newSr = prev.length + 1;
        return [...prev, {
          ...createEmptyRow(newSr), product_id: product.id, design_code: product.design_code,
          designSearch: product.design_code, colors, sizes, product,
          color: matchedSku?.color || '', size: matchedSku?.size || '',
          source_bill: sourceBill, source_bill_date: sourceBillDate, source_entry_id: sourceEntryId,
          se_variants: seVariants,
        }];
      }
    });
    toast.success(`Scanned: ${product.design_code}${sourceBill ? ` (Bill: ${sourceBill})` : ''}`);
  }, []);

  const startScanner = useCallback(async () => {
    setScannerActive(true);
    const { Html5Qrcode } = await import('html5-qrcode');
    setTimeout(() => {
      const el = document.getElementById(scannerContainerId);
      if (!el) { setScannerActive(false); return; }
      const scanner = new Html5Qrcode(scannerContainerId);
      scannerRef.current = scanner;
      scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 250, height: 150 }, aspectRatio: 2.0 },
        (decodedText) => { handleScannedCode(decodedText); }, () => {}
      ).catch(() => { toast.error('Camera not available. Use wireless scanner.'); setScannerActive(false); });
    }, 200);
  }, [handleScannedCode]);

  const stopScanner = useCallback(() => {
    if (scannerRef.current) { try { scannerRef.current.stop().then(() => { try { scannerRef.current.clear(); } catch(e) {} }).catch(() => {}); } catch(e) {} scannerRef.current = null; }
    setScannerActive(false);
  }, []);

  useEffect(() => { return () => { if (scannerRef.current) { try { scannerRef.current.stop().catch(() => {}); } catch(e) {} scannerRef.current = null; } }; }, []);

  // Auto-focus first design search on load
  const [dataLoaded, setDataLoaded] = useState(false);
  useEffect(() => {
    if (dataLoaded) {
      setTimeout(() => { const input = document.querySelector('[data-testid="sr-design-search-0"]'); if (input) input.focus(); }, 300);
    }
  }, [dataLoaded]);

  useEffect(() => {
    const handleClick = (e) => {
      if (customerRef.current && !customerRef.current.contains(e.target)) setCustomerDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    const handleClick = () => setRows(prev => prev.map(r => ({ ...r, showDesignDropdown: false })));
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [custRes, prodRes, agentRes, distRes] = await Promise.all([
          api.get('/customers'), api.get('/products'),
          api.get('/agents'), api.get('/distributors'),
        ]);
        setCustomers(custRes.data.filter(c => c.is_active));
        setProducts(prodRes.data);
        setAgents(agentRes.data || []);
        setDistributors(distRes.data || []);

        if (!editSR) {
          const cnRes = await api.get('/sales-returns/next-credit-note');
          setCreditNoteNumber(cnRes.data.credit_note_number);
        }

        if (editSR) prefillEdit(custRes.data, prodRes.data, editSR);
        setDataLoaded(true);
      } catch { toast.error('Failed to load data'); }
    };
    load();
  }, []);

  // Load customer-specific designs when customer changes
  useEffect(() => {
    if (!selectedCustomer) { setCustomerDesigns(null); return; }
    api.get(`/sales-returns/customer-designs/${selectedCustomer}`)
      .then(res => setCustomerDesigns(res.data || []))
      .catch(() => setCustomerDesigns(null));
  }, [selectedCustomer]);


  const prefillEdit = (custs, prods, sr) => {
    const cust = custs.find(c => c.id === sr.customer_id);
    if (cust) {
      setSelectedCustomer(cust.id);
      setCustomerSearch(cust.company_name + (cust.city ? ` (${cust.city})` : ''));
    }
    setReturnDate(sr.return_date || new Date().toISOString().split('T')[0]);
    setCreditNoteNumber(sr.credit_note_number || '');
    setReturnNotes(sr.notes || '');
    setBillType(sr.bill_type || 'invoice');
    setFreightCharges(sr.freight_charges || '');

    const editRows = [];
    let srNum = 1;
    for (const item of (sr.items || [])) {
      const prod = prods.find(p => p.id === item.product_id);
      const _sc = prod ? (prod.skus || []).map(s => s.color).filter(Boolean) : [];
      const _ic = prod ? (Array.isArray(prod.image_colors) ? prod.image_colors : (prod.image_colors && typeof prod.image_colors === 'object' ? Object.values(prod.image_colors).filter(Boolean) : [])) : [];
      const colors = [...new Set([..._sc, ..._ic])];
      const sizes = prod ? [...new Set((prod.skus || []).map(s => s.size))] : [];
      // Merge variants with same color+size (handles duplicate entries)
      const variantMap = {};
      for (const v of (item.variants || [])) {
        const vKey = `${v.color}_${v.size}`;
        if (variantMap[vKey]) {
          variantMap[vKey].qty_returned = (variantMap[vKey].qty_returned || 0) + (v.qty_returned || 0);
        } else {
          variantMap[vKey] = { ...v };
        }
      }
      for (const v of Object.values(variantMap)) {
        // Build size groups/individual sizes for the edit row
        const seVars = (item.se_variants || item.variants || []).filter(sv => sv.color === v.color);
        let sizeGroups = [], individualSizes = [];
        if (seVars.length > 0) {
          const byPrice = {};
          seVars.forEach(sv => {
            const price = sv.wsp || sv.rate || 0;
            if (!byPrice[price]) byPrice[price] = [];
            byPrice[price].push(sv.size);
          });
          sizeGroups = Object.entries(byPrice)
            .filter(([, szs]) => szs.length > 1)
            .map(([price, szs]) => ({
              id: `set_${szs[0]}_${szs[szs.length - 1]}`,
              label: `Set: ${szs[0]}-${szs[szs.length - 1]}`,
              sizes: szs, rate: parseFloat(price), count: szs.length,
            }));
          individualSizes = seVars.map(sv => ({ size: sv.size, rate: sv.wsp || sv.rate || 0 }));
        } else if (prod) {
          const r = getSizeGroups(prod, v.color);
          sizeGroups = r.groups; individualSizes = r.individualSizes;
        }
        editRows.push({
          sr: srNum, product_id: item.product_id, design_code: item.design_code,
          designSearch: item.design_code, color: v.color, size: v.size,
          qty: v.qty_returned || 0, reason: v.reason || 'defective', notes: v.notes || '',
          rate: v.rate || v.wsp || '', discount: v.discount_percent || '',
          colors, sizes, product: prod, showDesignDropdown: false,
          sizeGroups, individualSizes,
          source_bill: item.source_se_number || '', source_bill_date: '',
          source_entry_id: item.source_entry_id || '',
        });
        srNum++;
      }
    }
    if (editRows.length === 0) editRows.push(createEmptyRow(1));
    setRows(editRows);
  };

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer.id);
    setCustomerSearch(customer.company_name + (customer.city ? ` (${customer.city})` : ''));
    setCustomerDropdownOpen(false);
  };

  const filteredCustomers = useMemo(() => {
    if (!customerSearch.trim()) return customers.slice(0, 20);
    const q = customerSearch.trim().toLowerCase();
    return customers.filter(c =>
      c.company_name?.toLowerCase().includes(q) || c.city?.toLowerCase().includes(q) || (/\d/.test(customerSearch) && c.phone?.includes(q))
    ).slice(0, 20);
  }, [customerSearch, customers]);

  const SIZE_ORDER = ['S', 'M', 'L', 'XL', '2XL', 'XXL', '3XL', 'XXXL', '4XL', '5XL', 'Free', 'Free Size'];
  const sortScannedRows = (rows) => {
    const filled = rows.filter(r => r.product_id);
    const empty = rows.filter(r => !r.product_id);
    filled.sort((a, b) => {
      const dA = (a.design_code || '').toUpperCase(), dB = (b.design_code || '').toUpperCase();
      if (dA !== dB) return dA.localeCompare(dB, undefined, { numeric: true });
      const cA = (a.color || '').toUpperCase(), cB = (b.color || '').toUpperCase();
      if (cA !== cB) return cA.localeCompare(cB);
      const sA = SIZE_ORDER.indexOf(a.size), sB = SIZE_ORDER.indexOf(b.size);
      return (sA === -1 ? 999 : sA) - (sB === -1 ? 999 : sB);
    });
    return [...filled.map((r, i) => ({ ...r, sr: i + 1 })), ...empty.map((r, i) => ({ ...r, sr: filled.length + i + 1 }))];
  };

  const updateRow = (idx, field, value) => {
    setRows(prev => {
      const next = [...prev];
      const row = { ...next[idx] };
      row[field] = value;

      // SKU scan detection
      if (field === 'designSearch' && value && value.length >= 3) {
        const searchUpper = value.trim().toUpperCase();
        let matchedProduct = null, matchedSku = null;
        for (const p of products) { const sku = (p.skus || []).find(s => s.sku_code?.toUpperCase() === searchUpper); if (sku) { matchedProduct = p; matchedSku = sku; break; } }
        if (!matchedProduct && (searchUpper.includes('_') || searchUpper.includes('-'))) { const parts = searchUpper.split(/[_-]/); if (parts.length >= 2) { matchedProduct = products.find(p => p.design_code?.toUpperCase() === parts[0]); if (matchedProduct) matchedSku = matchedProduct.skus?.find(s => s.sku_code?.toUpperCase() === searchUpper) || null; } }
        if (matchedProduct && matchedSku) {
          // Check duplicate
          const existingIdx = next.findIndex((r, i) => i !== idx && r.product_id === matchedProduct.id && r.color === matchedSku.color && r.size === matchedSku.size);
          if (existingIdx >= 0) {
            const existing = { ...next[existingIdx] };
            existing.qty = (parseFloat(existing.qty) || 0) + 1;
            next[existingIdx] = existing;
            row.designSearch = '';
            next[idx] = row;
            toast.success(`${matchedProduct.design_code} | ${matchedSku.color} | ${matchedSku.size} → Qty: ${existing.qty}`);
            const sorted = sortScannedRows(next);
            setTimeout(() => { const inputs = document.querySelectorAll('[data-testid^="sr-design-search-"]'); const last = inputs[inputs.length - 1]; if (last) last.focus(); }, 100);
            return sorted;
          }
          const colors = [...new Set([...(matchedProduct.skus || []).map(s => s.color).filter(Boolean), ...(Array.isArray(matchedProduct.image_colors) ? matchedProduct.image_colors : (matchedProduct.image_colors && typeof matchedProduct.image_colors === 'object' ? Object.values(matchedProduct.image_colors).filter(Boolean) : []))])];
          const sizes = [...new Set((matchedProduct.skus || []).map(s => s.size))];
          row.product_id = matchedProduct.id; row.design_code = matchedProduct.design_code; row.designSearch = matchedProduct.design_code;
          row.colors = colors; row.sizes = sizes; row.product = matchedProduct; row.showDesignDropdown = false;
          row.color = matchedSku.color; row.size = matchedSku.size; row.qty = 1; row.reason = 'defective';
          next[idx] = row;
          toast.success(`Scanned: ${matchedProduct.design_code} | ${matchedSku.color} | ${matchedSku.size}`);
          next.push(createEmptyRow(next.length + 1));
          const sorted = sortScannedRows(next);
          setTimeout(() => { const inputs = document.querySelectorAll('[data-testid^="sr-design-search-"]'); const last = inputs[inputs.length - 1]; if (last) last.focus(); }, 100);
          return sorted;
        }
      }

      if (field === 'designSearch' && row.design_code && value !== row.design_code) {
        row.product_id = ''; row.design_code = ''; row.color = ''; row.size = '';
        row.colors = []; row.sizes = []; row.sizeGroups = []; row.individualSizes = [];
        row.product = null; row.qty = ''; row.rate = ''; row.discount = ''; row.se_variants = [];
      }
      if (field === 'color' && row.product) {
        // Use SE variant sizes if available, otherwise fall back to product master
        const seColorVars = (row.se_variants || []).filter(v => v.color === value);
        let groups, individualSizes;
        if (seColorVars.length > 0) {
          const byPrice = {};
          seColorVars.forEach(v => {
            const price = v.wsp || 0;
            if (!byPrice[price]) byPrice[price] = [];
            byPrice[price].push(v.size);
          });
          groups = Object.entries(byPrice)
            .filter(([, sizes]) => sizes.length > 1)
            .map(([price, sizes]) => ({
              id: `set_${sizes[0]}_${sizes[sizes.length - 1]}`,
              label: `Set: ${sizes[0]}-${sizes[sizes.length - 1]}`,
              sizes, rate: parseFloat(price), count: sizes.length,
            }));
          individualSizes = seColorVars.map(v => ({ size: v.size, rate: v.wsp || 0 }));
        } else {
          ({ groups, individualSizes } = getSizeGroups(row.product, value));
        }
        row.sizeGroups = groups;
        row.individualSizes = individualSizes;
        row.sizes = individualSizes.map(s => s.size);
        if (groups.length > 0) {
          row.size = groups[0].id;
        } else if (individualSizes.length > 0) {
          row.size = individualSizes[0].size;
        }
        // Auto-fill rate/discount from SE
        const seVar = seColorVars.length > 0 ? seColorVars[0] : null;
        if (seVar) {
          row.rate = seVar.wsp || '';
          row.discount = seVar.discount_percent || '';
        }
      }
      if (field === 'size' && row.se_variants?.length > 0 && row.color) {
        // Check if this specific size has a different rate
        const seVar = (row.se_variants || []).find(v => v.color === row.color && v.size === value);
        if (seVar) {
          row.rate = seVar.wsp || '';
          row.discount = seVar.discount_percent || '';
        }
      }
      next[idx] = row;
      return next;
    });
  };

  const selectDesign = async (idx, product) => {
    const allColors = [...new Set([...(product.skus || []).map(s => s.color).filter(Boolean), ...(Array.isArray(product.image_colors) ? product.image_colors : (product.image_colors && typeof product.image_colors === 'object' ? Object.values(product.image_colors).filter(Boolean) : []))])];
    const sizes = [...new Set((product.skus || []).map(s => s.size))];

    let sourceBill = '', sourceBillDate = '', sourceEntryId = '', seVariants = [];
    try {
      const params = selectedCustomer ? `?customer_id=${selectedCustomer}` : '';
      const res = await api.get(`/sales-returns/lookup-design/${product.design_code}${params}`);
      if (res.data.found) {
        sourceBill = res.data.entry_number || res.data.invoice_number || '';
        sourceBillDate = res.data.entry_date || '';
        sourceEntryId = res.data.entry_id || '';
        seVariants = res.data.se_variants || [];
      }
    } catch { /* ignore */ }

    // Filter colors to only those sold in SE
    const seColors = [...new Set(seVariants.map(v => v.color))];
    const colors = seColors.length > 0 ? allColors.filter(c => seColors.includes(c)) : allColors;

    // Auto-select color if only one, and cascade to size/rate
    let autoColor = '', autoSizeGroups = [], autoIndividualSizes = [], autoSize = '', autoRate = '', autoDiscount = '';
    if (colors.length === 1) {
      autoColor = colors[0];
      // Use SE variant sizes if available
      const seColorVars = seVariants.filter(v => v.color === autoColor);
      if (seColorVars.length > 0) {
        const byPrice = {};
        seColorVars.forEach(v => {
          const price = v.wsp || 0;
          if (!byPrice[price]) byPrice[price] = [];
          byPrice[price].push(v.size);
        });
        autoSizeGroups = Object.entries(byPrice)
          .filter(([, sizes]) => sizes.length > 1)
          .map(([price, sizes]) => ({
            id: `set_${sizes[0]}_${sizes[sizes.length - 1]}`,
            label: `Set: ${sizes[0]}-${sizes[sizes.length - 1]}`,
            sizes, rate: parseFloat(price), count: sizes.length,
          }));
        autoIndividualSizes = seColorVars.map(v => ({ size: v.size, rate: v.wsp || 0 }));
      } else {
        const { groups, individualSizes } = getSizeGroups(product, autoColor);
        autoSizeGroups = groups;
        autoIndividualSizes = individualSizes;
      }
      if (autoSizeGroups.length > 0) autoSize = autoSizeGroups[0].id;
      else if (autoIndividualSizes.length > 0) autoSize = autoIndividualSizes[0].size;
      const seVar = seVariants.find(v => v.color === autoColor);
      if (seVar) { autoRate = seVar.wsp || ''; autoDiscount = seVar.discount_percent || ''; }
    }

    setRows(prev => {
      const next = [...prev];
      next[idx] = {
        ...next[idx], product_id: product.id, design_code: product.design_code,
        designSearch: product.design_code, colors, sizes, product,
        showDesignDropdown: false,
        color: autoColor, size: autoSize,
        sizeGroups: autoSizeGroups, individualSizes: autoIndividualSizes,
        rate: autoRate, discount: autoDiscount,
        source_bill: sourceBill, source_bill_date: sourceBillDate, source_entry_id: sourceEntryId,
        se_variants: seVariants,
      };
      return next;
    });
  };

  const addRow = () => setRows(prev => [...prev, createEmptyRow(prev.length + 1)]);
  const removeRow = (idx) => {
    if (rows.length <= 1) return;
    setRows(prev => prev.filter((_, i) => i !== idx).map((r, i) => ({ ...r, sr: i + 1 })));
  };

  const filteredDesigns = (search) => {
    let base = products;
    // Filter to only show designs sold to this customer
    if (customerDesigns && customerDesigns.length > 0) {
      const dcSet = new Set(customerDesigns);
      base = base.filter(p => dcSet.has(p.design_code));
    } else if (customerDesigns !== null && customerDesigns.length === 0 && selectedCustomer) {
      return []; // Customer has no SE designs
    }
    if (!search.trim()) return base.slice(0, 15);
    const q = search.trim().toLowerCase();
    return base.filter(p => p.design_code.toLowerCase().includes(q) || p.category?.toLowerCase().includes(q)).slice(0, 15);
  };

  const submitReturn = async () => {
    if (!selectedCustomer) { toast.error('Select a customer'); return; }
    const validRows = rows.filter(r => r.product_id && r.color && r.size && parseInt(r.qty) > 0);
    if (validRows.length === 0) { toast.error('Add at least one item with color, size, and qty'); return; }

    const itemsMap = {};
    for (const r of validRows) {
      const key = `${r.product_id}_${r.source_entry_id || 'none'}`;
      if (!itemsMap[key]) {
        itemsMap[key] = {
          product_id: r.product_id, design_code: r.design_code,
          source_entry_id: r.source_entry_id || null,
          source_se_number: r.source_bill || null,
          variants: [],
        };
      }
      const totalQty = parseInt(r.qty) || 0;
      if (r.size === 'both') {
        (r.sizeGroups || []).forEach(grp => {
          grp.sizes.forEach(size => {
            if (totalQty > 0) {
              itemsMap[key].variants.push({
                size, color: r.color, qty_returned: totalQty,
                reason: r.reason || 'defective', notes: r.notes || null,
                rate: parseFloat(r.rate) || 0, discount_percent: parseFloat(r.discount) || 0,
              });
            }
          });
        });
      } else {
        const grp = (r.sizeGroups || []).find(g => g.id === r.size);
        if (grp) {
          grp.sizes.forEach(size => {
            if (totalQty > 0) {
              itemsMap[key].variants.push({
                size, color: r.color, qty_returned: totalQty,
                reason: r.reason || 'defective', notes: r.notes || null,
                rate: parseFloat(r.rate) || 0, discount_percent: parseFloat(r.discount) || 0,
              });
            }
          });
        } else if (r.size && totalQty > 0) {
          itemsMap[key].variants.push({
            size: r.size, color: r.color, qty_returned: totalQty,
            reason: r.reason || 'defective', notes: r.notes || null,
            rate: parseFloat(r.rate) || 0, discount_percent: parseFloat(r.discount) || 0,
          });
        }
      }
    }

    // Deduplicate variants: merge same size+color by summing qty
    for (const item of Object.values(itemsMap)) {
      const vMap = {};
      for (const v of item.variants) {
        const vKey = `${v.size}_${v.color}`;
        if (vMap[vKey]) {
          vMap[vKey].qty_returned += v.qty_returned;
        } else {
          vMap[vKey] = { ...v };
        }
      }
      item.variants = Object.values(vMap);
    }

    const payload = {
      customer_id: selectedCustomer,
      return_date: returnDate,
      source_entry_id: validRows[0]?.source_entry_id || null,
      credit_note_number: creditNoteNumber || null,
      bill_type: billType,
      items: Object.values(itemsMap),
      notes: returnNotes || null,
      freight_charges: parseFloat(freightCharges) || 0,
    };

    setSaving(true);
    try {
      if (editSR) {
        await api.put(`/sales-returns/${editSR.id}`, {
          return_date: returnDate,
          credit_note_number: creditNoteNumber || null,
          bill_type: billType,
          items: Object.values(itemsMap),
          notes: returnNotes || null,
          freight_charges: parseFloat(freightCharges) || 0,
        });
        toast.success(`Return ${editSR.return_number} updated! Stock adjusted.`);
        clearDraft();
        navigate(`/sales-return/${editSR.id}`);
      } else {
        const res = await api.post('/sales-returns', payload);
        toast.success(`Return ${res.data.return_number} created! Credit Note: ${creditNoteNumber}`);
        clearDraft();
        navigate('/sales-returns');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save return');
    } finally { setSaving(false); }
  };

  const grandQty = rows.reduce((s, r) => {
    const q = parseInt(r.qty) || 0;
    if (r.size === 'both') {
      return s + (r.sizeGroups || []).reduce((sum, g) => sum + q * g.count, 0);
    }
    const grp = (r.sizeGroups || []).find(g => g.id === r.size);
    return s + (grp ? q * grp.count : q);
  }, 0);

  return (
    <div className="p-6 space-y-4 animate-fadeIn" data-testid="create-sr-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>
            {editSR ? `Edit ${editSR.return_number}` : 'New Sales Return'}
          </h1>
          <p className="text-gray-500 text-sm">Process customer return — Stock will be added back</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/sales-returns')} data-testid="sr-back-btn">Cancel</Button>
          <Button onClick={submitReturn} disabled={saving} className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700" data-testid="sr-save-btn">
            {saving ? 'Saving...' : editSR ? 'Update Return' : 'Create Return'}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative" ref={customerRef}>
              <Label className="text-xs font-semibold text-gray-600">Customer *</Label>
              <Input value={customerSearch}
                onChange={e => { setCustomerSearch(e.target.value); setCustomerDropdownOpen(!!e.target.value.trim()); if (!e.target.value) setSelectedCustomer(''); }}
                placeholder="Search customer..." className="h-9 text-sm" data-testid="sr-customer-search"
                disabled={!!editSR}
              />
              {customerDropdownOpen && filteredCustomers.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredCustomers.map(c => (
                    <div key={c.id} className="px-3 py-2 hover:bg-orange-50 cursor-pointer text-sm"
                      onClick={() => selectCustomer(c)} data-testid={`sr-customer-option-${c.id}`}>
                      <span className="font-medium">{c.company_name}</span>
                      {c.city && <span className="text-gray-400 ml-2">({c.city})</span>}
                      <span className="text-gray-400 ml-1 text-xs">— {c.contact_person}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-600">Return Date</Label>
              <Input type="date" value={returnDate} onChange={e => setReturnDate(e.target.value)} className="h-9 text-sm" data-testid="sr-return-date" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-600">Bill Type</Label>
              <select
                value={billType}
                onChange={e => setBillType(e.target.value)}
                className="w-full h-9 px-3 border border-gray-300 rounded-md text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="sr-bill-type"
              >
                <option value="invoice">Invoice</option>
                <option value="challan">Challan</option>
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-600">Credit Note No.</Label>
              <Input value={creditNoteNumber} onChange={e => setCreditNoteNumber(e.target.value)} placeholder="CN-2526-001" className="h-9 text-sm font-semibold text-orange-700" data-testid="sr-credit-note" readOnly />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-600">Notes</Label>
              <Input value={returnNotes} onChange={e => setReturnNotes(e.target.value)} placeholder="Return notes..." className="h-9 text-sm" data-testid="sr-notes" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Agent / Distributor info */}
      {selectedCustomer && (() => {
        const cust = customers.find(c => c.id === selectedCustomer);
        const agentName = agents.find(a => a.id === cust?.agent_id)?.name;
        const distName = distributors.find(d => d.id === cust?.distributor_id)?.name;
        return (agentName || distName) ? (
          <div className="flex gap-6 text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-lg">
            {agentName && <span>Agent: <strong>{agentName}</strong></span>}
            {distName && <span>Distributor: <strong>{distName}</strong></span>}
          </div>
        ) : null;
      })()}

      {/* Scanner Section */}
      <Card className="border-orange-200">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="flex items-center gap-1.5 text-xs text-orange-600 font-medium">
              <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
              Wireless Scanner Ready — scan any barcode
            </span>
            <div className="h-4 w-px bg-gray-300" />
            {scannerActive ? (
              <Button variant="destructive" size="sm" onClick={stopScanner} className="h-7 gap-1.5 text-xs" data-testid="sr-scanner-toggle">
                <CameraOff className="w-3 h-3" /> Stop Camera
              </Button>
            ) : (
              <Button variant="ghost" size="sm" onClick={startScanner} className="h-7 gap-1.5 text-xs text-gray-500" data-testid="sr-scanner-toggle">
                <Camera className="w-3 h-3" /> Live Camera
              </Button>
            )}
          </div>
          <div className={`mt-3 flex justify-center ${!scannerActive ? 'hidden' : ''}`}>
            <div id={scannerContainerId} className="w-full max-w-sm rounded-lg overflow-hidden border border-orange-200" data-testid="sr-scanner-view" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sr-invoice-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-3 py-2 text-left w-12 font-semibold text-gray-600">Sr</th>
                  <th className="px-3 py-2 text-left min-w-[180px] font-semibold text-gray-600">Design No</th>
                  <th className="px-3 py-2 text-left w-32 font-semibold text-gray-600">Last Bill #</th>
                  <th className="px-3 py-2 text-left w-28 font-semibold text-gray-600">Bill Date</th>
                  <th className="px-3 py-2 text-left min-w-[120px] font-semibold text-gray-600">Colour</th>
                  <th className="px-3 py-2 text-left w-24 font-semibold text-gray-600">Size</th>
                  <th className="px-3 py-2 text-right w-20 font-semibold text-gray-600">Rate</th>
                  <th className="px-3 py-2 text-right w-16 font-semibold text-gray-600">Disc%</th>
                  <th className="px-3 py-2 text-right w-20 font-semibold text-gray-600">Ret Qty</th>
                  <th className="px-3 py-2 text-left w-28 font-semibold text-gray-600">Reason</th>
                  <th className="px-3 py-2 text-left w-28 font-semibold text-gray-600">Notes</th>
                  <th className="px-3 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50/50" data-testid={`sr-row-${idx}`}>
                    <td className="px-3 py-1.5 text-gray-500 font-mono">{row.sr}</td>
                    <td className="px-3 py-1.5 relative">
                      <Input value={row.designSearch}
                        onChange={e => { updateRow(idx, 'designSearch', e.target.value); updateRow(idx, 'showDesignDropdown', true); }}
                        onFocus={() => { if (row.designSearch) updateRow(idx, 'showDesignDropdown', true); }}
                        placeholder="Search design..." className="h-8 text-sm" data-testid={`sr-design-search-${idx}`}
                        data-row={idx} data-col={0} onKeyDown={handleTableKeyDown}
                      />
                      {row.showDesignDropdown && row.designSearch && (
                        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto left-0">
                          {filteredDesigns(row.designSearch).map(p => (
                            <div key={p.id} className="px-3 py-2 hover:bg-orange-50 cursor-pointer text-sm flex items-center gap-2"
                              onMouseDown={(e) => { e.preventDefault(); selectDesign(idx, p); }} data-testid={`sr-design-option-${p.id}`}>
                              <span className="font-mono font-semibold">{p.design_code}</span>
                              <span className="text-gray-400 text-xs">{p.category}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-1.5">
                      <span className="text-xs font-medium text-blue-700" data-testid={`sr-bill-ref-${idx}`}>
                        {row.source_bill || <span className="text-gray-300">—</span>}
                      </span>
                    </td>
                    <td className="px-3 py-1.5">
                      <span className="text-xs text-gray-500" data-testid={`sr-bill-date-${idx}`}>
                        {row.source_bill_date ? (() => { const d = new Date(row.source_bill_date); return isNaN(d) ? row.source_bill_date : `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getFullYear()).slice(-2)}`; })() : <span className="text-gray-300">—</span>}
                      </span>
                    </td>
                    <td className="px-3 py-1.5">
                      <select value={row.color} onChange={e => updateRow(idx, 'color', e.target.value)}
                        className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                        disabled={!row.product_id} data-testid={`sr-color-${idx}`}
                        data-row={idx} data-col={1} onKeyDown={handleTableKeyDown}>
                        <option value="">Select</option>
                        {(row.colors || []).map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-1.5">
                      <select value={row.size} onChange={e => updateRow(idx, 'size', e.target.value)}
                        className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                        disabled={!row.color} data-testid={`sr-size-${idx}`}
                        data-row={idx} data-col={2} onKeyDown={handleTableKeyDown}>
                        <option value="">Select</option>
                        {(row.sizeGroups || []).length > 1 && (() => {
                          const allSizes = (row.sizeGroups || []).flatMap(g => g.sizes);
                          return <option value="both">{allSizes[0]} to {allSizes[allSizes.length - 1]} ({allSizes.length}pcs)</option>;
                        })()}
                        {(row.sizeGroups || []).length > 0 && <optgroup label="Sets">
                          {(row.sizeGroups || []).map(g => <option key={g.id} value={g.id}>{g.label} ({g.count}pcs)</option>)}
                        </optgroup>}
                        <optgroup label="Individual">
                          {(row.individualSizes || []).map(s => <option key={s.size} value={s.size}>{s.size}</option>)}
                        </optgroup>
                      </select>
                    </td>
                    <td className="px-3 py-1.5">
                      <Input type="number" min="0" value={row.rate} onChange={e => updateRow(idx, 'rate', e.target.value)}
                        className="h-8 text-right text-sm" onWheel={e => e.target.blur()} data-testid={`sr-rate-${idx}`}
                        placeholder="Rate" data-row={idx} data-col={3} onKeyDown={handleTableKeyDown} />
                    </td>
                    <td className="px-3 py-1.5">
                      <Input type="number" min="0" max="100" value={row.discount} onChange={e => updateRow(idx, 'discount', e.target.value)}
                        className="h-8 text-right text-sm w-16" onWheel={e => e.target.blur()} data-testid={`sr-disc-${idx}`}
                        placeholder="%" data-row={idx} data-col={4} onKeyDown={handleTableKeyDown} />
                    </td>
                    <td className="px-3 py-1.5">
                      <Input type="number" min="0" value={row.qty} onChange={e => updateRow(idx, 'qty', e.target.value)}
                        className="h-8 text-right text-sm" onWheel={e => e.target.blur()} data-testid={`sr-qty-${idx}`}
                        data-row={idx} data-col={5} onKeyDown={handleTableKeyDown} />
                    </td>
                    <td className="px-3 py-1.5">
                      <select value={row.reason} onChange={e => updateRow(idx, 'reason', e.target.value)}
                        className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                        data-testid={`sr-reason-${idx}`}
                        data-row={idx} data-col={4} onKeyDown={handleTableKeyDown}>
                        {RETURN_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-1.5">
                      <Input value={row.notes || ''} onChange={e => updateRow(idx, 'notes', e.target.value)}
                        placeholder="Optional" className="h-8 text-xs" data-testid={`sr-item-notes-${idx}`}
                        data-row={idx} data-col={5} onKeyDown={handleTableKeyDown} />
                    </td>
                    <td className="px-3 py-1.5">
                      {rows.length > 1 && (
                        <button onClick={() => removeRow(idx)} className="text-red-400 hover:text-red-600" data-testid={`sr-remove-row-${idx}`}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 border-t-2">
                <tr>
                  <td colSpan="6" className="px-3 py-2">
                    <Button variant="outline" size="sm" onClick={addRow} className="text-xs" data-testid="sr-add-row">
                      <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                      Add Row
                    </Button>
                  </td>
                  <td className="px-3 py-2 text-right font-bold text-orange-700 text-base" data-testid="sr-grand-qty">{grandQty}</td>
                  <td colSpan="3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Freight Charges */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Label className="text-sm font-semibold text-gray-700">Freight Charges</Label>
            <Input type="number" min="0" step="0.01" value={freightCharges}
              onChange={e => setFreightCharges(e.target.value)}
              placeholder="0" className="h-9 w-40 text-right text-sm"
              onWheel={e => e.target.blur()} data-testid="sr-freight-charges" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateSalesReturn;
